import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Save, User2, Target, Dumbbell, Salad, Sparkles, AlertTriangle, History, ChevronDown, ChevronUp, Download, TrendingDown, X, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { ThemeToggle } from '../../components/ThemeToggle';
import {
  Select as ShadSelect, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';

const NUMERIC_FIELDS = ['age', 'weight', 'height', 'target_weight', 'body_fat', 'training_frequency', 'training_duration', 'meals_per_day'];
const CLEARABLE_TEXT_FIELDS = ['diet_preferences', 'allergies', 'fasting_window', 'training_time'];

const GOALS = (en) => [
  { v: 'weight_loss', l: en ? 'Fat loss' : 'Perte de gras' },
  { v: 'recomposition', l: en ? 'Body recomp' : 'Recomposition corporelle' },
  { v: 'maintenance', l: en ? 'Maintenance' : 'Maintien' },
  { v: 'muscle_gain', l: en ? 'Muscle gain' : 'Prise de muscle' },
];
const PACE = (en) => [
  { v: 'gentle', l: en ? 'Gentle' : 'Doux' }, { v: 'moderate', l: en ? 'Moderate' : 'Modéré' }, { v: 'aggressive', l: en ? 'Aggressive' : 'Agressif' },
];
const ACTIVITY = (en) => [
  { v: 'sedentary', l: en ? 'Sedentary' : 'Sédentaire' }, { v: 'light', l: en ? 'Light' : 'Légère' },
  { v: 'moderate', l: en ? 'Moderate' : 'Modérée' }, { v: 'active', l: en ? 'Active' : 'Active' }, { v: 'very_active', l: en ? 'Very active' : 'Très active' },
];
const TRAINING_TYPES = (en) => [
  { v: 'musculation', l: en ? 'Strength training' : 'Musculation' }, { v: 'crossfit', l: 'CrossFit' },
  { v: 'natation', l: en ? 'Swimming' : 'Natation' }, { v: 'course', l: en ? 'Running' : 'Course à pied' },
  { v: 'velo', l: en ? 'Cycling' : 'Vélo' }, { v: 'marche', l: en ? 'Brisk walking' : 'Marche rapide' },
  { v: 'boxe', l: en ? 'Boxing' : 'Boxe' }, { v: 'hiit', l: 'HIIT' }, { v: 'circuit', l: 'Circuit training' },
  { v: 'ems', l: 'EMS' }, { v: 'yoga', l: 'Yoga / Pilates' }, { v: 'other', l: en ? 'Other' : 'Autre' },
];
const INTENSITY = (en) => [{ v: 'light', l: en ? 'Light' : 'Légère' }, { v: 'moderate', l: en ? 'Moderate' : 'Modérée' }, { v: 'intense', l: en ? 'Intense' : 'Intense' }];
const LEVEL = (en) => [{ v: 'beginner', l: en ? 'Beginner' : 'Débutant' }, { v: 'intermediate', l: en ? 'Intermediate' : 'Intermédiaire' }, { v: 'advanced', l: en ? 'Advanced' : 'Avancé' }];
const RECOVERY = (en) => [{ v: 'good', l: en ? 'Good' : 'Bonne' }, { v: 'medium', l: en ? 'Average' : 'Moyenne' }, { v: 'poor', l: en ? 'Poor' : 'Faible' }];
const COOKING = (en) => [{ v: 'express', l: 'Express (<10 min)' }, { v: 'normal', l: 'Normal (15-30 min)' }, { v: 'love', l: en ? 'I love cooking' : "J'aime cuisiner" }];
const EATING_PATTERN = (en) => [{ v: 'classic', l: en ? 'Classic' : 'Classique' }, { v: 'intermittent', l: en ? 'Intermittent fasting' : 'Jeûne intermittent' }];
const MENOPAUSE = (en) => [{ v: 'no', l: en ? 'No' : 'Non' }, { v: 'peri', l: en ? 'Perimenopause' : 'Périménopause' }, { v: 'meno', l: en ? 'Menopause' : 'Ménopause' }];
const CYCLE = (en) => [{ v: 'yes', l: en ? 'Yes' : 'Oui' }, { v: 'no', l: en ? 'No / not sure' : 'Non / je ne sais pas' }];

const MISSING_FIELD_LABELS = (en) => ({
  gender: en ? 'gender' : 'sexe', age: en ? 'age' : 'âge', height: en ? 'height' : 'taille',
  goal: en ? 'goal' : 'objectif', activity_level: en ? 'daily activity' : 'activité quotidienne',
  training_frequency: en ? 'sessions/week' : 'séances/semaine', training_intensity: en ? 'training intensity' : 'intensité d\'entraînement',
  training_duration: en ? 'session duration' : 'durée de séance', weight: en ? 'weight' : 'poids',
});

const formatHistoryValue = (field, value, en) => {
  const maps = { goal: GOALS(en), training_type: TRAINING_TYPES(en), training_intensity: INTENSITY(en), training_level: LEVEL(en) };
  if (maps[field]) { const opt = maps[field].find((o) => o.v === value); return opt ? opt.l : value; }
  if (field === 'training_frequency') return `${value} ${en ? 'sessions/wk' : 'séances/sem.'}`;
  if (field === 'training_duration') return `${value} min`;
  return String(value);
};

const Select = ({ label, value, onChange, options, testId }) => (
  <div>
    <label className="text-xs text-zinc-500 mb-1 block">{label}</label>
    <ShadSelect value={value || undefined} onValueChange={onChange}>
      <SelectTrigger
        className="w-full bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_20px_-10px_rgba(217,70,239,0.3)] rounded-lg px-3 py-2 h-auto text-white text-sm focus:ring-primary focus:border-primary"
        data-testid={testId}
      >
        <SelectValue placeholder="—" />
      </SelectTrigger>
      <SelectContent className="bg-zinc-900 border border-white/10 text-white">
        {options.map((o) => (
          <SelectItem key={o.v} value={o.v} className="focus:bg-primary/20 focus:text-white">{o.l}</SelectItem>
        ))}
      </SelectContent>
    </ShadSelect>
  </div>
);

const NumberInput = ({ label, value, onChange, unit, testId, step }) => (
  <div>
    <label className="text-xs text-zinc-500 mb-1 block">{label} {unit ? `(${unit})` : ''}</label>
    <input type="number" inputMode="decimal" step={step || 1} value={value ?? ''} onChange={(e) => onChange(e.target.value)}
      className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-primary focus:outline-none"
      data-testid={testId} />
  </div>
);

/** Profil vivant du client (calculateur) : poids/taille/âge/objectif/entraînement/préférences,
 * éditable à tout moment, synchronisé partout (Progression, macros, futurs calculs). */
export const ProfileCard = ({ api, authFetch, en, onChanged, onWeightAlert, plateauSuggestion, onDismissPlateau }) => {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [cardioOptin, setCardioOptin] = useState(false);
  const [cardioOptinBusy, setCardioOptinBusy] = useState(false);

  const load = useCallback(() => {
    api('/api/tracking/profile').then((d) => { setProfile(d); setForm(d); }).catch(() => setProfile({}));
    api('/api/tracking/profile/history').then((d) => setHistory(d.history || [])).catch(() => {});
    authFetch('/api/tracking/cardio-pro/leaderboard/settings').then((r) => r.json()).then((d) => setCardioOptin(!!d.optin)).catch(() => {});
  }, [api, authFetch]);
  useEffect(() => { load(); }, [load]);

  const toggleCardioOptin = () => {
    setCardioOptinBusy(true);
    const next = !cardioOptin;
    authFetch('/api/tracking/cardio-pro/leaderboard/settings', { method: 'PUT', body: JSON.stringify({ optin: next }) })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(() => { setCardioOptin(next); toast.success(next ? (en ? 'You now appear in the Cardio leaderboard!' : 'Vous apparaissez maintenant dans le classement Cardio !') : (en ? 'You left the Cardio leaderboard.' : 'Vous avez quitté le classement Cardio.')); })
      .catch(() => toast.error(en ? 'Error' : 'Erreur.'))
      .finally(() => setCardioOptinBusy(false));
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.keys(form).forEach((k) => {
        if (['sex', 'name', 'has_profile', 'missing_fields', 'updated_at', 'email'].includes(k)) return;
        const v = form[k];
        if (v === undefined || v === profile?.[k]) return;
        if (v === '' && !CLEARABLE_TEXT_FIELDS.includes(k)) return;
        payload[k] = NUMERIC_FIELDS.includes(k) ? Number(v) : v;
      });
      if (Object.keys(payload).length === 0) { toast.info(en ? 'Nothing changed' : 'Rien à enregistrer'); setSaving(false); return; }
      const res = await api('/api/tracking/profile', { method: 'PUT', body: JSON.stringify(payload) });
      setProfile(res.profile); setForm(res.profile);
      toast.success(en ? 'Profile saved and applied everywhere' : 'Profil enregistré et appliqué partout', {
        description: res.goals ? (en ? `New targets: ${res.goals.calories} kcal` : `Nouveaux objectifs : ${res.goals.calories} kcal`) : undefined,
      });
      onWeightAlert && onWeightAlert(res.weight_alert);
      onChanged && onChanged();
      api('/api/tracking/profile/history').then((d) => setHistory(d.history || [])).catch(() => {});
    } catch {
      toast.error(en ? 'Save failed' : "Échec de l'enregistrement");
    } finally { setSaving(false); }
  };

  const downloadProfilePdf = async () => {
    setPdfBusy(true);
    try {
      const res = await authFetch('/api/tracking/profile/pdf');
      if (!res.ok) throw new Error('failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'mon-profil-body-up.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(en ? 'PDF download failed.' : 'Échec du téléchargement du PDF.');
    } finally { setPdfBusy(false); }
  };

  const applySuggestedPace = async () => {
    if (!plateauSuggestion?.suggested_deficit_surplus) return;
    setSaving(true);
    try {
      const res = await api('/api/tracking/profile', { method: 'PUT', body: JSON.stringify({ deficit_surplus: plateauSuggestion.suggested_deficit_surplus }) });
      setProfile(res.profile); setForm(res.profile);
      toast.success(en ? 'Goal adjusted' : 'Objectif ajusté', {
        description: res.goals ? (en ? `New targets: ${res.goals.calories} kcal` : `Nouveaux objectifs : ${res.goals.calories} kcal`) : undefined,
      });
      onChanged && onChanged();
      onDismissPlateau && onDismissPlateau();
    } catch {
      toast.error(en ? 'Save failed' : "Échec de l'enregistrement");
    } finally { setSaving(false); }
  };

  if (profile === null) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-5 space-y-6" data-testid="profile-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="flex items-center gap-2 font-bold text-white mb-1"><User2 className="w-4 h-4 text-primary" /> {en ? 'My profile' : 'Mon profil'}</h3>
          <p className="text-xs text-zinc-500">{en ? "Edit anytime — synced everywhere on the app (weight, calories, macros, next calculation)." : "Modifiable à tout moment — synchronisé partout sur l'app (poids, calories, macros, prochain calcul)."}</p>
        </div>
        <button onClick={downloadProfilePdf} disabled={pdfBusy}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/20 text-white text-xs font-bold rounded-xl transition-colors disabled:opacity-50"
          data-testid="download-profile-pdf-btn">
          {pdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} {en ? 'Download PDF' : 'Télécharger le PDF'}
        </button>
      </div>

      <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-black/30 border border-white/10" data-testid="account-info-block">
        <div className="min-w-0">
          <p className="text-sm font-bold text-white truncate">{profile?.name || (en ? 'My account' : 'Mon compte')}</p>
          <p className="text-xs text-zinc-500 truncate">{profile?.email}</p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-black/30 border border-white/10" data-testid="display-preferences-block">
        <div>
          <p className="text-sm font-bold text-white">{en ? 'Display' : 'Affichage'}</p>
          <p className="text-xs text-zinc-500">{en ? 'Light or dark theme, across the whole app.' : "Thème clair ou sombre, sur toute l'app."}</p>
        </div>
        <ThemeToggle />
      </div>

      <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-black/30 border border-white/10" data-testid="cardio-leaderboard-optin-block">
        <div className="flex items-start gap-2.5">
          <Crown className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-white">{en ? 'Cardio (AMRAP) leaderboard' : 'Classement Cardio (AMRAP)'}</p>
            <p className="text-xs text-zinc-500">{en ? 'Optional — only your first name is visible to others.' : 'Optionnel — seul votre prénom sera visible des autres membres.'}</p>
          </div>
        </div>
        <button onClick={toggleCardioOptin} disabled={cardioOptinBusy}
          className={`shrink-0 w-12 h-7 rounded-full transition-colors relative ${cardioOptin ? 'bg-orange-500' : 'bg-zinc-700'}`}
          data-testid="profile-cardio-leaderboard-optin-toggle">
          <span className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${cardioOptin ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      {plateauSuggestion && (
        <div className="flex items-start gap-2.5 p-4 rounded-xl bg-purple-500/10 border border-purple-500/25" data-testid="plateau-suggestion-detail">
          <TrendingDown className="w-4 h-4 text-purple-300 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-purple-300 uppercase tracking-wide mb-1">{en ? 'Plateau detected' : 'Plateau détecté'}</p>
            <p className="text-sm text-zinc-200 leading-snug">{plateauSuggestion.message}</p>
            <p className="text-[11px] text-zinc-500 mt-1">
              {en ? `Weight stable around ${plateauSuggestion.weight_avg}kg for ${plateauSuggestion.days_covered} days.` : `Poids stable autour de ${plateauSuggestion.weight_avg}kg depuis ${plateauSuggestion.days_covered} jours.`}
            </p>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {plateauSuggestion.suggested_deficit_surplus && (
                <button onClick={applySuggestedPace} disabled={saving}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-purple-500 hover:bg-purple-600 disabled:opacity-50"
                  data-testid="plateau-apply-suggestion-btn">
                  {en ? `Apply: ${PACE(en).find((p) => p.v === plateauSuggestion.suggested_deficit_surplus)?.l || plateauSuggestion.suggested_deficit_surplus}` : `Appliquer : ${PACE(en).find((p) => p.v === plateauSuggestion.suggested_deficit_surplus)?.l || plateauSuggestion.suggested_deficit_surplus}`}
                </button>
              )}
              <button onClick={onDismissPlateau} className="px-3 py-1.5 rounded-lg text-xs font-bold text-zinc-300 border border-white/15 hover:bg-white/5 inline-flex items-center gap-1"
                data-testid="plateau-dismiss-btn">
                <X className="w-3 h-3" /> {en ? 'Dismiss' : 'Ignorer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {(profile.missing_fields || []).length > 0 && (
        <div className="flex items-start gap-2.5 p-3 rounded-xl bg-sky-500/10 border border-sky-500/25" data-testid="profile-incomplete-reminder">
          <AlertTriangle className="w-4 h-4 text-sky-400 shrink-0 mt-0.5" />
          <p className="text-xs text-sky-200">
            {en ? 'Missing for accurate targets: ' : 'Manquant pour des objectifs précis : '}
            <strong>{profile.missing_fields.map((f) => MISSING_FIELD_LABELS(en)[f] || f).join(', ')}</strong>
          </p>
        </div>
      )}

      <div>
        <p className="text-xs font-bold text-zinc-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><User2 className="w-3.5 h-3.5" /> {en ? 'Base profile' : 'Profil de base'}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Select label={en ? 'Gender' : 'Sexe'} value={form.gender} onChange={(v) => set('gender', v)}
            options={[{ v: 'female', l: en ? 'Female' : 'Femme' }, { v: 'male', l: en ? 'Male' : 'Homme' }]} testId="profile-gender-select" />
          <NumberInput label={en ? 'Age' : 'Âge'} value={form.age} onChange={(v) => set('age', v)} testId="profile-age-input" />
          <NumberInput label={en ? 'Height' : 'Taille'} unit="cm" value={form.height} onChange={(v) => set('height', v)} testId="profile-height-input" />
          <NumberInput label={en ? 'Weight' : 'Poids'} unit="kg" step={0.1} value={form.weight} onChange={(v) => set('weight', v)} testId="profile-weight-input" />
          <NumberInput label={en ? 'Target weight' : 'Poids cible'} unit="kg" step={0.1} value={form.target_weight} onChange={(v) => set('target_weight', v)} testId="profile-target-weight-input" />
          <NumberInput label={en ? 'Body fat' : '% Masse grasse'} unit="%" step={0.5} value={form.body_fat} onChange={(v) => set('body_fat', v)} testId="profile-bodyfat-input" />
        </div>
        <p className="text-[11px] text-amber-300/80 mt-2">{en ? 'Changing weight here also updates it in Progress and recalculates your calories/macros.' : 'Changer le poids ici le met aussi à jour dans Progression et recalcule vos calories/macros.'}</p>
      </div>

      <div>
        <p className="text-xs font-bold text-zinc-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Target className="w-3.5 h-3.5" /> {en ? 'Goal' : 'Objectif'}</p>
        <div className="grid grid-cols-2 gap-3">
          <Select label={en ? 'Goal' : 'Objectif'} value={form.goal} onChange={(v) => set('goal', v)} options={GOALS(en)} testId="profile-goal-select" />
          <Select label={en ? 'Pace' : 'Intensité'} value={form.deficit_surplus} onChange={(v) => set('deficit_surplus', v)} options={PACE(en)} testId="profile-pace-select" />
        </div>
      </div>

      <div>
        <p className="text-xs font-bold text-zinc-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Dumbbell className="w-3.5 h-3.5" /> {en ? 'Activity & training' : 'Activité & entraînement'}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Select label={en ? 'Daily activity' : 'Activité quotidienne'} value={form.activity_level} onChange={(v) => set('activity_level', v)} options={ACTIVITY(en)} testId="profile-activity-select" />
          <NumberInput label={en ? 'Sessions/week' : 'Séances/semaine'} value={form.training_frequency} onChange={(v) => set('training_frequency', v)} testId="profile-frequency-input" />
          <NumberInput label={en ? 'Duration' : 'Durée'} unit="min" value={form.training_duration} onChange={(v) => set('training_duration', v)} testId="profile-duration-input" />
          <Select label={en ? 'Main activity' : 'Activité principale'} value={form.training_type} onChange={(v) => set('training_type', v)} options={TRAINING_TYPES(en)} testId="profile-training-type-select" />
          <Select label={en ? 'Intensity' : 'Intensité'} value={form.training_intensity} onChange={(v) => set('training_intensity', v)} options={INTENSITY(en)} testId="profile-intensity-select" />
          <Select label={en ? 'Level' : 'Niveau'} value={form.training_level} onChange={(v) => set('training_level', v)} options={LEVEL(en)} testId="profile-level-select" />
          <Select label={en ? 'Recovery' : 'Récupération'} value={form.recovery_quality} onChange={(v) => set('recovery_quality', v)} options={RECOVERY(en)} testId="profile-recovery-select" />
        </div>
      </div>

      <div>
        <p className="text-xs font-bold text-zinc-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Salad className="w-3.5 h-3.5" /> {en ? 'Food preferences' : 'Préférences alimentaires'}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
          <NumberInput label={en ? 'Meals/day' : 'Repas/jour'} value={form.meals_per_day} onChange={(v) => set('meals_per_day', v)} testId="profile-meals-input" />
          <Select label={en ? 'Eating pattern' : 'Rythme alimentaire'} value={form.eating_pattern} onChange={(v) => set('eating_pattern', v)} options={EATING_PATTERN(en)} testId="profile-pattern-select" />
          <Select label={en ? 'Cooking time' : 'Temps cuisine'} value={form.cooking_time} onChange={(v) => set('cooking_time', v)} options={COOKING(en)} testId="profile-cooking-select" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">{en ? 'Dietary preferences' : 'Préférences alimentaires'}</label>
            <input value={form.diet_preferences || ''} onChange={(e) => set('diet_preferences', e.target.value)}
              placeholder={en ? 'Vegetarian, vegan...' : 'Végétarien, végan...'}
              className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid="profile-diet-input" />
          </div>
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">{en ? 'Allergies' : 'Allergies'}</label>
            <input value={form.allergies || ''} onChange={(e) => set('allergies', e.target.value)}
              placeholder={en ? 'Nuts, lactose...' : 'Fruits à coque, lactose...'}
              className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm" data-testid="profile-allergies-input" />
          </div>
        </div>
      </div>

      {form.gender === 'female' && (
        <div>
          <p className="text-xs font-bold text-zinc-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> {en ? 'For women' : 'Pour les femmes'}</p>
          <div className="grid grid-cols-2 gap-3">
            <Select label={en ? 'Menopause' : 'Ménopause'} value={form.menopause} onChange={(v) => set('menopause', v)} options={MENOPAUSE(en)} testId="profile-menopause-select" />
            {form.menopause !== 'meno' && (
              <Select label={en ? 'Regular cycle' : 'Cycle régulier'} value={form.regular_cycle} onChange={(v) => set('regular_cycle', v)} options={CYCLE(en)} testId="profile-cycle-select" />
            )}
          </div>
        </div>
      )}

      <button onClick={save} disabled={saving}
        className="w-full py-2.5 rounded-lg font-bold text-white bg-gradient-to-r from-primary to-pink-500 disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="profile-save-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {en ? 'Save changes' : 'Enregistrer les modifications'}
      </button>

      {history.length > 0 && (
        <div className="border-t border-white/10 pt-4">
          <button onClick={() => setHistoryOpen((o) => !o)} className="w-full flex items-center justify-between text-xs font-bold text-zinc-400 uppercase tracking-wide" data-testid="profile-history-toggle">
            <span className="flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> {en ? 'Change history' : "Historique des changements"}</span>
            {historyOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          {historyOpen && (
            <div className="mt-3 space-y-2" data-testid="profile-history-list">
              {history.map((h) => (
                <div key={h.history_id} className="text-xs text-zinc-400 flex items-center gap-1.5 flex-wrap">
                  <span className="text-zinc-500">{new Date(h.changed_at).toLocaleDateString(en ? 'en-GB' : 'fr-CH', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                  <span className="text-zinc-300 font-semibold">{h.field_label}</span> :
                  <span className="text-zinc-500 line-through">{formatHistoryValue(h.field, h.old_value, en)}</span>
                  <span className="text-primary">→</span>
                  <span className="text-white">{formatHistoryValue(h.field, h.new_value, en)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileCard;
